// Base HTTP client (DEC-141).
//
// The session is an `HttpOnly` cookie the browser attaches itself, so there is
// nothing for this module to read, store or send. `getToken`/`setToken`/
// `clearToken` and the JWT decoding that used to live here are gone with it:
// an HttpOnly cookie is unreadable from JavaScript by design, which is the
// property that makes an XSS unable to steal a session.
//
// Identity now comes from `GET /auth/me` via `AuthProvider`, not from parsing a
// token client-side.

export type Profile = 'engineering' | 'executive' | 'product_gtm' | 'admin'

export interface AuthClaims {
  tenant_id: string
  tenant_name: string
  user_id: string | null
  email: string | null
  name: string | null
  profile: Profile | null
  /** DEC-204: true only for an address the server lists as Super Admin. */
  is_super_admin?: boolean
}

/** Paths that must never trigger the global redirect below. */
const AUTH_PROBE_PATHS = [
  '/auth/me',
  '/auth/logout',
  '/auth/link/redeem',
  '/auth/login/request',
  // DEC-202: the sign-up screen is public; a refusal there is shown on that screen.
  '/auth/signup/preview',
  '/auth/signup/complete',
]

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // `credentials: 'include'` is what sends the session cookie. Without it the
  // browser withholds it and every request is anonymous.
  const resp = await fetch(path, { ...options, headers, credentials: 'include' })

  if (!resp.ok) {
    const detail = await resp
      .json()
      .then((b) => b?.detail ?? resp.statusText)
      .catch(() => resp.statusText)

    // Global 401 handler: hard-navigate to /login. There is no longer a token to
    // clear client-side — the server has already rejected the cookie.
    //
    // **This now covers a genuinely revoked session, not only an expired one.**
    // Before DEC-141 a disabled member kept full access until their JWT expired
    // on its own, and nothing here could tell the difference.
    //
    // The auth endpoints are excluded: /auth/me returning 401 is the normal
    // answer for a signed-out visitor, and redirecting on it would put the login
    // page into a reload loop with itself.
    if (resp.status === 401 && !AUTH_PROBE_PATHS.includes(path)) {
      window.location.href = '/login'
    }

    throw new ApiError(resp.status, typeof detail === 'string' ? detail : JSON.stringify(detail))
  }

  // 204 No Content
  if (resp.status === 204) return undefined as T

  return resp.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
